/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import { addTimingFromPerformanceMark as reactUFOAddTimingFromPerformanceMark } from '@atlaskit/react-ufo/initial-page-load-extra-timing';
import { browserMetrics } from '@atlassian/browser-metrics/lib';

/**
 * `performance.measure` will throw if start/stop aren't set.
 *
 * This call sets a user-timing on the profiler timelines so we can see sections of the load.
 */
const safeMeasure = (name: string, start: string, stop: string) => {
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		performance.measure(name, start, stop);
		// oxlint-disable-next-line no-empty
	} catch (_err: any) {} // oxlint-disable-line @typescript-eslint/no-explicit-any
};
export const setInitialPageLoadTimingFromPerformanceMarks = (
	name: string,
	start: string,
	stop: string,
	cleanStart = false,
	cleanStop = false,
): void => {
	if (__SERVER__) {
		return;
	}
	safeMeasure(name, start, stop);
	browserMetrics.experimental__addPageLoadTimingFromPerformanceMarks(name, start, stop);
	reactUFOAddTimingFromPerformanceMark(name, start, stop, cleanStart, cleanStop);
};
export const stopInitialPageLoadTimingFromPerformanceMarkStart = (
	name: string,
	start: string,
	cleanStart = false,
): void => {
	if (__SERVER__) {
		return;
	}
	const stopMark = `${name}__stop_mark$`;
	performance.mark(stopMark);
	safeMeasure(name, start, stopMark);
	browserMetrics.experimental__addPageLoadTimingFromPerformanceMarks(name, start, stopMark);
	reactUFOAddTimingFromPerformanceMark(name, start, stopMark, cleanStart, true);
};
/**
 * Enhances client-side performance analysis by measuring the time taken for initial page loads.
 * This function is designed to wrap around any given logic, providing insights into performance bottlenecks and opportunities for optimization.
 */
export const measureInitialPageLoadTiming = <T,>(name: string, fn: () => T): T => {
	if (__SERVER__) {
		return fn();
	}
	const startMark = `${name}__start_mark$`;
	const stopMark = `${name}__stop_mark$`;
	performance.mark(startMark);
	const result = fn();
	performance.mark(stopMark);
	safeMeasure(name, startMark, stopMark);
	browserMetrics.experimental__addPageLoadTimingFromPerformanceMarks(name, startMark, stopMark);
	reactUFOAddTimingFromPerformanceMark(name, startMark, stopMark, true, true);
	return result;
};
export const measureInitialPageLoadTimingAsync = async <T,>(
	name: string,
	fn: () => Promise<T>,
): Promise<T> => {
	if (__SERVER__) {
		return fn();
	}
	const startMark = `${name}__start_mark$`;
	const stopMark = `${name}__stop_mark$`;
	performance.mark(startMark);
	const result = await fn();
	performance.mark(stopMark);
	browserMetrics.experimental__addPageLoadTimingFromPerformanceMarks(name, startMark, stopMark);
	reactUFOAddTimingFromPerformanceMark(name, startMark, stopMark, true, true);
	return result;
};

export const setProductBundleReadyForInitialRender = (): void => {
	if (__SERVER__) {
		return;
	}
	stopInitialPageLoadTimingFromPerformanceMarkStart(
		'product.bundle-ready',
		'jira-spa.js:eval-stop',
	);
};
